import type { Keyword } from "@/types/keyword";
import type { KeywordTheme } from "@/types/keyword-theme";
import { isKeywordMetricPending } from "@/utils/keywordPendingDisplay";

export type ExportMode = "all" | "master" | "themed";
export type ExportFormat = "csv" | "tsv" | "json";

function esc(val: unknown, delimiter: string): string {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes(delimiter) || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function trendPct(kw: Keyword): string {
  const v = kw.search_volume_trend;
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

const COLUMNS = [
  "Keyword",
  "Theme",
  "Head Term",
  "Hero Rank",
  "KPS",
  "Search Vol.",
  "Relevance",
  "Trend %",
  "PPC Max Bid",
  "Opp. Tag",
  "Master List",
];

function keywordToRow(
  kw: Keyword,
  themeName: string,
  isHeadTerm: boolean,
): Record<string, unknown> {
  return {
    "Keyword": kw.search,
    "Theme": themeName,
    "Head Term": isHeadTerm ? "Yes" : "",
    "Hero Rank": kw.hero_rank ?? "",
    "KPS": kw.kps != null ? Math.round(kw.kps) : "",
    "Search Vol.": kw.search_volume ?? "",
    // AUT-376: exports the raw relevance the Rel. column shows. Guarded on the -1 sentinel
    // because the raw column carries it for a keyword pending enrichment (BR21) where
    // hero_relevance_adjusted was simply null -- without this the CSV would gain "-1" cells.
    "Relevance": kw.hero_relevance != null && !isKeywordMetricPending(kw.hero_relevance)
      ? Math.round(kw.hero_relevance)
      : "",
    "Trend %": trendPct(kw),
    "PPC Max Bid": kw.ppc_max_bid ?? "",
    "Opp. Tag": kw.opportunity_tag ?? "",
    "Master List": kw.is_master_list ? "Yes" : "No",
  };
}

interface FlatEntry {
  kw: Keyword;
  themeName: string;
  isHeadTerm: boolean;
}

function buildFlatEntries(
  candidates: Keyword[],
  kwThemeMap: Map<number, { name: string; isHeadTerm: boolean }>,
): FlatEntry[] {
  return candidates.map((kw) => {
    const info = kwThemeMap.get(kw.id);
    return { kw, themeName: info?.name ?? "Unthemed", isHeadTerm: info?.isHeadTerm ?? false };
  });
}

function buildGroupedEntries(
  candidates: Keyword[],
  themes: KeywordTheme[],
  kwById: Map<number, Keyword>,
  mode: ExportMode,
): FlatEntry[] {
  const result: FlatEntry[] = [];
  const candidateSet = new Set(candidates);

  for (const theme of themes) {
    for (const tk of theme.keywords) {
      const kw = kwById.get(tk.keyword_id);
      if (!kw) continue;
      if (!candidateSet.has(kw)) continue;
      result.push({ kw, themeName: theme.name, isHeadTerm: tk.is_head_term });
    }
  }

  // Unthemed at the end (not for "themed" mode)
  if (mode !== "themed") {
    const themedIds = new Set(themes.flatMap((t) => t.keywords.map((k) => k.keyword_id)));
    for (const kw of candidates) {
      if (!themedIds.has(kw.id)) {
        result.push({ kw, themeName: "Unthemed", isHeadTerm: false });
      }
    }
  }

  return result;
}

function exportDelimited(entries: FlatEntry[], format: "csv" | "tsv", filename: string): void {
  const delimiter = format === "tsv" ? "\t" : ",";
  const header = COLUMNS.map((c) => esc(c, delimiter)).join(delimiter);
  const body = entries
    .map(({ kw, themeName, isHeadTerm }) => {
      const r = keywordToRow(kw, themeName, isHeadTerm);
      return COLUMNS.map((c) => esc(r[c], delimiter)).join(delimiter);
    })
    .join("\n");
  const mime = format === "tsv" ? "text/tab-separated-values" : "text/csv;charset=utf-8;";
  triggerDownload(new Blob([`${header}\n${body}`], { type: mime }), filename);
}

function exportJson(entries: FlatEntry[], grouped: boolean, filename: string): void {
  let data: unknown;
  if (grouped) {
    const map: Record<string, object[]> = {};
    for (const { kw, themeName, isHeadTerm } of entries) {
      if (!map[themeName]) map[themeName] = [];
      map[themeName].push(keywordToRow(kw, themeName, isHeadTerm));
    }
    data = map;
  } else {
    data = entries.map(({ kw, themeName, isHeadTerm }) =>
      keywordToRow(kw, themeName, isHeadTerm),
    );
  }
  triggerDownload(
    new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
    filename,
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportKeywordsToCsv(
  allKeywords: Keyword[],
  themes: KeywordTheme[],
  mode: ExportMode,
  groupByTheme: boolean,
  format: ExportFormat = "csv",
): void {
  const kwById = new Map(allKeywords.map((k) => [k.id, k]));
  const kwThemeMap = new Map<number, { name: string; isHeadTerm: boolean }>();
  for (const theme of themes) {
    for (const tk of theme.keywords) {
      kwThemeMap.set(tk.keyword_id, { name: theme.name, isHeadTerm: tk.is_head_term });
    }
  }

  let candidates: Keyword[];
  switch (mode) {
    case "master":  candidates = allKeywords.filter((k) => k.is_master_list); break;
    case "themed":  candidates = allKeywords.filter((k) => kwThemeMap.has(k.id)); break;
    default:        candidates = allKeywords;
  }

  const date = new Date().toISOString().slice(0, 10);
  const scopeSlug = mode === "master" ? "master-list" : mode === "themed" ? "themed" : "all";
  const groupSlug = groupByTheme ? "-grouped" : "";
  const filename = `keyword-bank-${scopeSlug}${groupSlug}-${date}.${format}`;

  const entries = groupByTheme
    ? buildGroupedEntries(candidates, themes, kwById, mode)
    : buildFlatEntries(candidates, kwThemeMap);

  if (format === "json") {
    exportJson(entries, groupByTheme, filename);
  } else {
    exportDelimited(entries, format, filename);
  }
}
